import { useEffect, useState } from "react";
import { Loader2, Search, Users, Mail, Phone, Inbox } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { formatPrice, formatDate } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

const STATUS_LABEL = {
  pending: { es: "Pendiente", en: "Pending" }, processing: { es: "En proceso", en: "Processing" },
  shipped: { es: "Enviado", en: "Shipped" }, delivered: { es: "Entregado", en: "Delivered" },
  cancelled: { es: "Cancelado", en: "Cancelled" },
};
const STATUS_COLOR = {
  pending: "text-amber-500", processing: "text-blue-500", shipped: "text-indigo-500",
  delivered: "text-emerald-500", cancelled: "text-red-500",
};

export default function AdminClients() {
  const { lang } = useLanguage();
  const es = lang === "es";
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  useEffect(() => {
    api.get("/admin/clients").then((r) => setClients(r.data.clients)).finally(() => setLoading(false));
  }, []);

  const openClient = (c) => {
    setActive(c);
    setOrders([]);
    setOrdersLoading(true);
    api.get("/admin/client-requests", { params: { key: c.key || "", email: c.email || "", phone: c.phone || "" } })
      .then((r) => setOrders(r.data.requests))
      .finally(() => setOrdersLoading(false));
  };

  const statusLabel = (v) => (STATUS_LABEL[v] || {})[lang] || v;

  const filtered = clients.filter((c) => {
    const q = search.toLowerCase();
    return !q || (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q) || (c.phone || "").toLowerCase().includes(q);
  });

  return (
    <div data-testid="admin-clients">
      <div className="mb-8">
        <h1 className="font-serif text-4xl font-light">{es ? "Clientes" : "Clients"}</h1>
        <p className="text-muted-foreground text-sm mt-1">{clients.length} {es ? "clientes únicos" : "unique clients"}</p>
      </div>

      <div className="relative mb-5 max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={es ? "Buscar cliente" : "Search client"} className="rounded-none pl-9" data-testid="client-search" />
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        {loading ? (
          <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="clients-empty">
            <Users className="h-8 w-8 mx-auto mb-3 opacity-40" />
            {es ? "Aún no hay clientes." : "No clients yet."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="p-4 font-medium overline">{es ? "Nombre" : "First name"}</th>
                <th className="p-4 font-medium overline hidden sm:table-cell">{es ? "Apellidos" : "Last name"}</th>
                <th className="p-4 font-medium overline">{es ? "Correo" : "Email"}</th>
                <th className="p-4 font-medium overline hidden md:table-cell">{es ? "Teléfono" : "Phone"}</th>
                <th className="p-4 font-medium overline text-center">{es ? "Solicitudes" : "Requests"}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} onClick={() => openClient(c)} className="border-b border-border last:border-0 hover:bg-secondary/40 cursor-pointer" data-testid={`client-row-${i}`}>
                  <td className="p-4 font-medium">{c.first_name || c.name}</td>
                  <td className="p-4 hidden sm:table-cell text-muted-foreground">{c.last_name}</td>
                  <td className="p-4 text-muted-foreground">{c.email || "—"}</td>
                  <td className="p-4 hidden md:table-cell text-muted-foreground">{c.phone || "—"}</td>
                  <td className="p-4 text-center"><span className="inline-flex items-center justify-center min-w-7 h-7 px-2 bg-gold/15 text-gold font-medium" data-testid={`client-requests-${i}`}>{c.requests_count}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="client-detail-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">{active.name || (es ? "Cliente" : "Client")}</DialogTitle>
                <DialogDescription className="flex flex-wrap gap-x-5 gap-y-1 pt-1">
                  {active.email && <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-gold" /> {active.email}</span>}
                  {active.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-gold" /> {active.phone}</span>}
                </DialogDescription>
              </DialogHeader>

              <div className="flex items-center gap-2 overline text-gold mt-1"><Inbox className="h-3.5 w-3.5" /> {es ? "Historial de solicitudes" : "Request history"} ({active.requests_count})</div>

              {ordersLoading ? (
                <div className="py-10 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-gold" /></div>
              ) : (
                <div className="border border-border divide-y divide-border" data-testid="client-orders">
                  {orders.map((o) => (
                    <div key={o.id} className="p-4" data-testid={`client-order-${o.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-medium">{o.order_number}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(o.created_at)} · {o.items?.length} {es ? "piezas" : "pieces"}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm">{formatPrice(o.subtotal)}</p>
                          <p className={`text-xs capitalize ${STATUS_COLOR[o.status] || ""}`}>{statusLabel(o.status)}</p>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {o.items?.map((it, idx) => (
                          <span key={idx} className="text-xs text-muted-foreground bg-secondary px-2 py-0.5">{it.name} ×{it.quantity}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
